import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { localApi, credentials } from "utils/imgApi";
import { makeStyles } from "@material-ui/core/styles";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import CustomInput from "components/CustomInput/CustomInput.js";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import { AiOutlineFolderView } from "react-icons/ai";
import SweetAlert from "react-bootstrap-sweetalert";
import styles from "assets/jss/material-dashboard-pro-react/views/userProfileStyles.js";
import styleSweet from "assets/jss/material-dashboard-pro-react/views/sweetAlertStyle.js";
import styDrop from "assets/jss/material-dashboard-pro-react/views/extendedFormsStyle.js";
import axios from "axios";
import ImageKit from "imagekit-javascript";

const useStyles = makeStyles(styles);
const useStyl = makeStyles(styDrop);
const useStyleSweet = makeStyles(styleSweet);

const INITIAL_TOUCHED = {
  publicKey: false,
  privateKey: false,
  urlEndPoint: false,
};

export default function AddImageKitAuth() {
  const [publicKey, setPublicKey] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [urlEndPoint, setUrlEndPoint] = useState("");

  const [alertType, setAlertType] = useState("");
  const [alertMsg, setAlertMsg] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [touched, setTouched] = useState(INITIAL_TOUCHED);

  const classes = useStyles();
  const classe = useStyl();
  const classSweet = useStyleSweet();

  const userObj = (() => {
    try {
      const sessionData = sessionStorage.getItem("sessionData");
      return sessionData ? JSON.parse(sessionData) : null;
    } catch (error) {
      console.error("Error parsing session data:", error);
      return null;
    }
  })();

  const validateForm = () => {
    const errors = {
      publicKey: !publicKey.trim(),
      privateKey: !privateKey.trim(),
      urlEndPoint: !urlEndPoint.trim(),
    };

    const isValid = !Object.values(errors).some(Boolean);

    // This makes every invalid field show its error after Save button click.
    setTouched({
      publicKey: true,
      privateKey: true,
      urlEndPoint: true,
    });

    return isValid;
  };

  const resetForm = () => {
    setPublicKey("");
    setPrivateKey("");
    setUrlEndPoint("");

    setTouched(INITIAL_TOUCHED);
  };

  const handleSave = async () => {
    if (loading) {
      return;
    }

    // Validate normal form fields
    const isFormValid = validateForm();

    if (!isFormValid) {
      setAlertType("error");
      setAlertMsg("Please fill all required fields.");
      // setSubmitted(true);
      return;
    }

    // Validate user
    if (!userObj?.id) {
      setAlertType("error");
      setAlertMsg("User information is missing. Please log in again.");
      setSubmitted(true);
      return;
    }

    setLoading(true);
    setSubmitted(false);

    try {
      const payload = {
        publicKey: publicKey?.trim() || "",
        privateKey: privateKey?.trim() || "",
        urlEndPoint: urlEndPoint?.trim() || "",
        adminId: userObj.id,
      };

      const response = await fetch(`${localApi}/imageskitauth/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Basic ${credentials}`,
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();

      let data;

      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        data = {
          message: responseText,
        };
      }

      //   console.log("Response:", response.status, data);

      // Already exists
      if (
        data?.message === "Already Exist !!!" ||
        responseText === "Already Exist !!!"
      ) {
        setAlertType("error");
        setAlertMsg("ImageKit details already exist for this admin.");
        setSubmitted(true);
        return;
      }

      // Successfully inserted
      if (
        data?.message === "Data Inserted Successfully !!!" ||
        responseText === "Data Inserted Successfully !!!"
      ) {
        setAlertType("success");
        setAlertMsg("ImageKit details saved successfully.");
        setSubmitted(true);
        return;
      }

      // Other backend error
      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.error ||
            responseText ||
            "Failed to save ImageKit details.",
        );
      }

      throw new Error(
        data?.message || responseText || "ImageKit details could not be saved.",
      );
    } catch (error) {
      console.error("ImageKit save error:", error);

      setAlertType("error");
      setAlertMsg(
        error?.message || "Failed to save ImageKit details. Please try again.",
      );
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const hideAlert = () => {
    setSubmitted(false);
    window.location.reload(false);
  };

  const getLabelColor = (field, value) => {
    const isEmpty = !value || (typeof value === "string" && !value.trim());

    return touched[field] && isEmpty ? "#f44336" : "#777";
  };

  return (
    <div>
      <GridContainer
        justify="center"
        alignItems="center"
        style={{ marginBottom: "5%" }}
      >
        <GridItem xs={12} sm={12} md={10} lg={8}>
          <Card>
            <CardHeader color="rose" icon>
              <CardIcon color="rose">
                <AiOutlineFolderView />
              </CardIcon>

              <h3 className={classes.cardIconTitle}>
                <small>Add Image Auth</small>
              </h3>
            </CardHeader>

            <CardBody>
              <GridContainer>
                {submitted && (
                  <SweetAlert
                    type={alertType}
                    style={{ display: "block", marginTop: "-100px" }}
                    title={alertMsg}
                    onConfirm={hideAlert}
                    confirmBtnCssClass={
                      classSweet.button + " " + classSweet.success
                    }
                  />
                )}

                {/* Public Key */}
                <GridItem xs={6} sm={6} md={6}>
                  <CustomInput
                    labelText={
                      <span
                        style={{ color: getLabelColor("publicKey", publicKey) }}
                      >
                        Public Key...
                      </span>
                    }
                    id="publicKey"
                    inputProps={{
                      placeholder: "e.g. avxhry",
                    }}
                    formControlProps={{
                      fullWidth: true,
                    }}
                    name="publicKey"
                    value={publicKey}
                    onChange={(event) => {
                      setPublicKey(event.target.value);
                      setTouched((previous) => ({
                        ...previous,
                        publicKey: true,
                      }));
                    }}
                    disabled={loading}
                  />
                </GridItem>

                {/* Private Key */}
                <GridItem xs={6} sm={6} md={6}>
                  <CustomInput
                    labelText={
                      <span
                        style={{
                          color: getLabelColor("privateKey", privateKey),
                        }}
                      >
                        Private Key...
                      </span>
                    }
                    id="privateKey"
                    inputProps={{
                      placeholder: "e.g. hyct12_3rh",
                    }}
                    formControlProps={{
                      fullWidth: true,
                    }}
                    name="privateKey"
                    value={privateKey}
                    onChange={(event) => {
                      setPrivateKey(event.target.value);
                      setTouched((previous) => ({
                        ...previous,
                        privateKey: true,
                      }));
                    }}
                    disabled={loading}
                  />
                </GridItem>

                {/* URL End Point */}
                <GridItem xs={12} sm={12} md={12}>
                  <CustomInput
                    labelText={
                      <span
                        style={{
                          color: getLabelColor("urlEndPoint", urlEndPoint),
                        }}
                      >
                        URL End Point...
                      </span>
                    }
                    id="urlEndPoint"
                    inputProps={{
                      placeholder: "e.g. hyct12_3rh",
                    }}
                    formControlProps={{
                      fullWidth: true,
                    }}
                    name="urlEndPoint"
                    value={urlEndPoint}
                    onChange={(event) => {
                      setUrlEndPoint(event.target.value);
                      setTouched((previous) => ({
                        ...previous,
                        urlEndPoint: true,
                      }));
                    }}
                    disabled={loading}
                  />
                </GridItem>
              </GridContainer>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 16,
                }}
              >
                <Button
                  color="rose"
                  onClick={handleSave}
                  disabled={loading}
                  style={{
                    opacity: loading ? 0.6 : 1,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "Saving..." : "Save Auth"}
                </Button>
              </div>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}

AddImageKitAuth.propTypes = {
  avatar: PropTypes.bool,
  addButtonProps: PropTypes.object,
  changeButtonProps: PropTypes.object,
  removeButtonProps: PropTypes.object,
};
